import './style.css';
import {Map, View} from 'ol';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Tile from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import Style from 'ol/style/Style.js';
import Icon from 'ol/style/Icon.js';
import Fill from 'ol/style/Fill.js';
import Text from 'ol/style/Text.js';
import Projection from 'ol/proj/Projection.js';
import proj4 from 'proj4';
import {register} from 'ol/proj/proj4.js';
import ScaleLine from 'ol/control/ScaleLine.js';
import {defaults as defaultControls} from 'ol/control/defaults.js';

// Importar url de los iconos de las estaciones, para que puedan ser advertidos por 'npm run build'
import marker_grey_url from './img/marker-grey.png';
import marker_01_url from './img/marker-01.png';
import marker_02_url from './img/marker-02.png';
import marker_03_url from './img/marker-03.png';
import marker_04_url from './img/marker-04.png';
import marker_05_url from './img/marker-05.png';
import marker_06_url from './img/marker-06.png';



const extent = [-2500000.0, 2900000.0, 1400000.0, 5800000.0];   // Área representada en el mapa
const centroPeninsula = [-350000.0, 4840000.0];   // Centro aproximado de la península en coordenadas EPSG:3857
const initPeninsulaZoom = 6.6;                    // Zoom inicial a la península
const centroCanarias = [-1756204.01,3300549.35];  // Centro aproximado de Canarias en coordenadas EPSG:3857
const initCanariasZoom = 8.0;                     // // Zoom inicial a Canarias
var REA_API_BASE_URL;
// Definir la URL base de la API, en función del valor de la variable de entorno del navegador REA_API_BASE_URL;
if (!(window.__ENV__?.REA_API_BASE_URL) || window.__ENV__?.REA_API_BASE_URL == "REA_API_BASE_URL_PLACEHOLDER") {
  REA_API_BASE_URL = 'http://localhost:4000';
}
else {
  REA_API_BASE_URL = window.__ENV__?.REA_API_BASE_URL;
}

const estacionesURL = REA_API_BASE_URL + "/mapa/estaciones";
const lineColors = {
  '10min': '#008400',
  '1h': '#17179f',
  '1d': '#b30000'
}

// Función para transformar timestamp en cadena con día del mes y nombre corto del mes.
const  toDayAndMonth = function(timestamp) {
  const date = new Date(timestamp);
  return date.getDate().toString() + ' ' + date.toLocaleString('es-ES', { month: 'short' });
}

// Función para seleccionar el icono que representa la estación dependiendo del valor de la tasa de radiación gamma
const iconFunction = function(feature) {
  let status = feature.get('status');
  if (!status.endsWith('_ok')) {
    return marker_grey_url;
  }
  let tasagamma = feature.get('tasagamma');
  if (tasagamma < 50) return marker_01_url;
  if (tasagamma < 100) return marker_02_url;
  if (tasagamma < 150) return marker_03_url;
  if (tasagamma < 200) return marker_04_url;
  if (tasagamma < 250) return marker_05_url;
  return marker_06_url;
};

// Función para calcular el z-index de los iconos de cada una de las estaciones.
// El objeto es que las estaciones con mayor tasa de radiación natural tengan mayor z-index
// para que se vean encima de las que tienen menor tasa.
const estacionesZIndex = function(feature) {
  let tasagamma = feature.get('tasagamma');
  if (tasagamma === null) return parseInt(0);
  return parseInt(10*tasagamma);
};

// Función para definir los estilos de la capa vectorial de estaciones
const estacionesStyleFunction = function (feature) {
  const style = new Style({
    image: new Icon({
      anchor: [0.5, 1.0],
      src: iconFunction(feature),
    }),
    text: new Text({
      text: feature.get('tasagamma') === null ? '': feature.get('tasagamma').toFixed(1),
      font: 'bold 14px "Open Sans", "Arial Unicode MS", "sans-serif"',
      scale: 1.1,
      placement: 'point',
      offsetY: -24,
      fill: new Fill({
        color: 'white',
      }),
    }),
    zIndex: estacionesZIndex(feature),
  })
  return style;
};

// Diccionario en formato geoJSON para definir la capa de estaciones de la REA.
// En producción se sustituirán las features por una URL consumiento la API de estaciones.
const geojsonEstaciones = {
  'type': 'FeatureCollection',
  'crs': {
    'type': 'name',
    'properties': {
      // 'name': 'EPSG:3857',   // WGS84 / Pseudo-Mercator
      // 'name': 'EPSG:4326',   // WGS84
      'name': 'EPSG:4258',   // ETRS89
      // 'units': 'm',      
      'units': 'degrees',
    },
  },
};

// Control de mapa para añdir una barra de escala
const mapcontrol = new ScaleLine({
  units: 'metric',
  bar: true,
  steps: 4,
  text: true,
  minWidth: 140,
});

const map = new Map({
  controls: defaultControls().extend([mapcontrol]),
  target: 'map',
  layers: [
  ],
  view: new View({
    extent: extent,
    center: centroPeninsula,
    zoom: initPeninsulaZoom,
    minZoom: 5,
    maxZoom: 20
  })
});

// Definir la proyección ETRS89, que no viene incluida por defecto en OpenLayers
proj4.defs("EPSG:4258", "+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs"); 
register(proj4);

// Definir la proyección en la que vienen las coordenadas de la capa de estaciones
const estcProjection = new Projection({
  code: geojsonEstaciones.crs.properties.name,
  units: geojsonEstaciones.crs.properties.units
});

const OSMlayer = new TileLayer({
  source: new OSM(),
  visible: true,
});

const OTMlayer = new Tile({
  source: new OSM({
    url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png'
  }),
  visible: false,
});

const PNOAlayer = new TileLayer({
  source: new TileWMS({
    url: 'https://www.ign.es/wms-inspire/pnoa-ma',
    crossOrigin: 'anonymous',
    attributions: 
      '© <a href="https://www.ign.es/web/ign/portal"' +
      'target="_blank">Sistema Cartográfico Nacional</a>',
    params: {
      'LAYERS': 'OI.OrthoimageCoverage',
      'FORMAT': 'image/jpeg',
    },
    serverType: 'mapserver',
  }),
  visible: false,
});

map.addLayer(OSMlayer);
map.addLayer(OTMlayer);
map.addLayer(PNOAlayer);

const estacionesSource = new VectorSource({
  url: estacionesURL,
  format: new GeoJSON({dataProjection: estcProjection, featureProjection: map.getView().getProjection()})
})

// Capa vectorial de estaciones
const estacionesLayer = new VectorLayer({
  source: estacionesSource,
  style: estacionesStyleFunction
});



// Añadir la capa de estaciones al mapa.
map.addLayer(estacionesLayer);

// Contenedor para el tooltip que se muestra haciendo hovering sobre las estaciones
const infoElement = document.getElementById('info');
infoElement.style.pointerEvents = 'none';

// Elemento con el tooltip
const tooltip = new bootstrap.Tooltip(infoElement, {
  animation: false,
  customClass: 'pe-none',
  offset: [0, 5],
  title: '-',
  trigger: 'manual',
});

// Estación sobre la que está situado el ratón
var currentFeature;

// Función para encontrar la estación sobre la que está el ratón y definir el contenido del tooltip
const displayFeatureInfo = function (pixel, target) {
  const feature = target.closest('.ol-control')
    ? undefined
    : map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature;
      });
  if (feature) {
    // const hit = map.hasFeatureAtPixel(pixel);
    // map.getTargetElement().style.cursor = hit ? 'pointer' : '';
    target.style.cursor = 'pointer';
    infoElement.style.left = pixel[0] + 'px';
    infoElement.style.top = pixel[1] + 'px';
    if (feature !== currentFeature) {
      tooltip.setContent({'.tooltip-inner': feature.get('name')});
    }
    if (currentFeature) {
      tooltip.update();
    } else {
      tooltip.show();
    }
  } else {
    target.style.cursor = '';
    tooltip.hide();
  }
  currentFeature = feature;
};

// Invocar la función que encuentra la estación cuando el ratón se sitúa encima
map.on('pointermove', function (evt) {
  if (evt.dragging) {
    tooltip.hide();
    currentFeature = undefined;
    return;
  }
  displayFeatureInfo(evt.pixel, evt.originalEvent.target);
});

// Función para mostrar la ventana que contiene las gráficas de series temporales cuando
// se hace click sobre una estación
map.on('singleclick', function (evt) {
  const feature = map.forEachFeatureAtPixel(evt.pixel, function (feature) {
    return feature;
  });
  if (!feature) {
    return;
  }
  buildChart(feature.getId(), '1h');
});

// Función para centrar el mapa en la península
function centrarPeninsula() {
  map.getView().setCenter(centroPeninsula);
  map.getView().setZoom(initPeninsulaZoom);
};

// Función para centrar el mapa en Canarias
function centrarCanarias() {
  map.getView().setCenter(centroCanarias);
  map.getView().setZoom(initCanariasZoom);
};


// Nodo del DOM con la ventana gráfica
const ventanaGrafica = document.getElementById("ventanaGrafica");

// Función para cerrar la ventana con las gráficas
function cerrarVentanaGrafica() {
  quitarDivOscura();
  quitarReloj();
  // ventanaGrafica.style.display = 'none';
  $( "#ventanaGrafica" ).fadeOut();
  estacionChart.clear();
  estacionChart.destroy();
  $( "#radiosbuton1h" ).prop('checked',true); //empezar siempre por el de 1h
};


// Nodo del canvas para alojar la gráfica chart.js
const chartContext = document.getElementById("estacionChart");
// Constante para alojar el objeto chart.js
var estacionChart;

// Función para mostrar las gráficas con las series temporales, usando chart.js
async function buildChart(id_estacion, seriesType) {
  tooltip.hide();
  mostrarReloj();
  currentFeature = estacionesSource.getFeatureById(id_estacion);
  const tasagamma = currentFeature.get('tasagamma')?currentFeature.get('tasagamma').toFixed(1):'';
  // $("#nombreEstacionGrafica").text("La tasa de radiación natural en " + currentFeature.get('name') + " es de " + gammarad?gammarad.toFixed(1):'' + " nSv/h");
  $("#nombreEstacionGrafica").text("Tasa de radiación gamma en " + currentFeature.get('name')  + ": " + tasagamma +  " nSv/h");
  if (estacionChart) {
    estacionChart.clear();
    estacionChart.destroy();
  }

  // Traer las series de datos mediante la API de estaciones de la REA.
  const reqString = REA_API_BASE_URL + "/mapa/estaciones/" + id_estacion + "/dataseries/" + seriesType;
  const response = await fetch(reqString);
  var dataseries = await response.json(); //extract JSON from the http response

  // Definir datasets para chart.js
  const chartData = {
    datasets: [
      {
        data: dataseries,  // datos a representar en la gráfica
        pointHitRadius: 5,  // radio alrededor del punto para activación del hovering
        pointHoverRadius: 5,  // radio del punto mostrado con el hovering
        borderColor: lineColors[seriesType],
        pointBackgroundColor:  lineColors[seriesType]
      }
    ]
  };

  // Configuración de la gráfica char.js
  const chartConfig = {
    type: 'line',
    data: chartData,
    options: {
      scales: {
        x: {
          type: 'linear',
          ticks: {
            callback: function(value, index, values) {
              const date = new Date(value);
              const min = values[0].value;
              const max = values[values.length - 1].value;
              const rangeMs = max - min;

              const ONE_MIN  = 60000;
              const ONE_HOUR = 3600000;
              const ONE_DAY  = 86400000;

              let interval;
              if      (rangeMs > 60 * ONE_DAY)  interval = 10 * ONE_DAY;
              else if (rangeMs > 20 * ONE_DAY)  interval = 5  * ONE_DAY;
              else if (rangeMs > 10 * ONE_DAY)  interval = 2  * ONE_DAY;
              else if (rangeMs > 4  * ONE_DAY)  interval = ONE_DAY;
              else if (rangeMs > 2  * ONE_DAY)  interval = 12 * ONE_HOUR;
              else if (rangeMs > 12 * ONE_HOUR) interval = 6  * ONE_HOUR;
              else if (rangeMs > 4  * ONE_HOUR) interval = 2  * ONE_HOUR;
              else if (rangeMs > 2  * ONE_HOUR) interval = ONE_HOUR;
              else if (rangeMs > 30 * ONE_MIN)  interval = 30 * ONE_MIN;
              else if (rangeMs > 10 * ONE_MIN)  interval = 10 * ONE_MIN;
              else                               interval = 5  * ONE_MIN;

              // Formatear según el intervalo, no según el rango
              if (interval >= ONE_DAY) {
                // Intervalo de días: mostrar siempre día y mes
                return date.getDate() + ' ' + date.toLocaleString('es-ES', { month: 'short' });
              } else {
                // Intervalo de horas o minutos: mostrar día/mes + hora:minuto
                const dia = date.getDate() + ' ' + date.toLocaleString('es-ES', { month: 'short' });
                const hora = date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0');
                return dia + ' ' + hora;
              }
            },
            font: { size: 16, weight: 'bold' }
          },
          afterBuildTicks: function(axis) {
            const min = axis.min;
            const max = axis.max;
            const rangeMs = max - min;

            const ONE_MIN  = 60000;
            const ONE_HOUR = 3600000;
            const ONE_DAY  = 86400000;

            // Elegir el intervalo más adecuado según el rango visible
            let interval;
            if      (rangeMs > 60 * ONE_DAY)  interval = 10 * ONE_DAY;
            else if (rangeMs > 20 * ONE_DAY)  interval = 5  * ONE_DAY;
            else if (rangeMs > 10 * ONE_DAY)  interval = 2  * ONE_DAY;
            else if (rangeMs > 4  * ONE_DAY)  interval = ONE_DAY;
            else if (rangeMs > 2  * ONE_DAY)  interval = 12 * ONE_HOUR;
            else if (rangeMs > 12 * ONE_HOUR) interval = 6  * ONE_HOUR;
            else if (rangeMs > 4  * ONE_HOUR) interval = 2  * ONE_HOUR;
            else if (rangeMs > 2  * ONE_HOUR) interval = ONE_HOUR;
            else if (rangeMs > 30 * ONE_MIN)  interval = 30 * ONE_MIN;
            else if (rangeMs > 10 * ONE_MIN)  interval = 10 * ONE_MIN;
            else                               interval = 5  * ONE_MIN;

            // Primer tick alineado al intervalo (redondeado hacia arriba desde min)
            const firstTick = Math.ceil(min / interval) * interval;

            const ticks = [];
            for (let t = firstTick; t <= max; t += interval) {
              ticks.push({ value: t });
            }

            axis.ticks = ticks;
          },
          grid: {
            lineWidth: 2
          }
        },
        y: {
          ticks: {
            font: {
              size: 16,
              weight: 'bold'
            }
          },
          title: {
            display: true,
            text: 'Tasa de radiación gamma (nSv/h)',
            font: {
              size: 18,
              weight: 'bold'
            }
          },
          grid: {
            lineWidth: 2
          }
        }
      },
      elements: {
        point: {
          radius: 0.0,
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          // display: true,
          // text: (chartData) => 'Point Style: ' + chartData.chart.data.datasets[0].pointStyle,
        },
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: function(context) {
              // let label = context.dataset.label;
              const date = new Date(context[0].parsed.x);
              const day = date.getDate().toString();
              const month = (date.getMonth() + 1).toString();
              const year = date.getFullYear().toString();
              let hours = date.getHours().toString();
              let minutes = date.getMinutes().toString();
              hours = hours.length < 2 ? '0'+hours : hours;
              minutes = minutes.length < 2 ? '0'+minutes : minutes;
              return day + '/' + month + '/' + year + ' ' + hours + ':' + minutes;
            },
            label: function(context) {
              return ' ' + context.parsed.y + ' nSv/h';
            },
          }
        },
        zoom: {
          pan: {
            enabled: true
          },
          zoom: {
            wheel: {
              enabled: true
            },
            drag: {
              enabled: true, // Enables the click-and-drag selection
              modifierKey: 'shift', // Set to 'ctrl', 'alt', or 'shift' if you want a modifier key required to drag
            },
          }
        }
      }
    }
  };

  // Objeto chart.js con la grafica, que se ubica en el elmento definido por 
  estacionChart = new Chart(chartContext, chartConfig);
  quitarReloj();
  mostrarDivOscura();
  // Mostrar la ventana contenedora
  $( '#ventanaGrafica' ).show();
  // ventanaGrafica.style.display = 'block';
};


function mostrarReloj(){
  $("#divReloj").show();
};
function quitarReloj(){
  $("#divReloj").hide();
};

function mostrarDivOscura() {
  $("#divOscura").show();
};

function quitarDivOscura() {
  $("#divOscura").hide();
};

function mostrarListaEstaciones() {
  $("#listaEstaciones").show();            // Mostar la tabla con la lista de estaciones
  $("#tablaEstaciones").DataTable().columns.adjust().draw();     // La primera vez que se muestra la tabla hay que ajustar ancho de columnas
};
function cerrarListaEstaciones() {
  $("#listaEstaciones").fadeOut();
  quitarReloj();
};

$(document).ready(function () {
  // Mostrar el número de estaciones en el panel de control cuando se hayan cargado las features de la capa vectorial
  estacionesSource.on('featuresloadend', function() {
    $('#totalEstaciones').text(estacionesSource.getFeatures().length);
  });


  // $('#totalEstaciones').text(features.length);


  // Botones para selección de serie temporal
  $('#radioButtons input:radio').click(function() {
    buildChart(currentFeature.getId(), $(this).val());
  });

  // Botón para cierre de ventana con gráfica
  $('#ventanaGraficaCierre, #divOscura').click(function() {
    cerrarVentanaGrafica();
  });

  // Botón para cierre de ventana con lista de estaciones
  $('#cierreListaEstaciones').click(function() {
    cerrarListaEstaciones();
  });

  // Mostrar lista de estaciones
  $('#enlaceListaEstaciones').click(function() {
    mostrarListaEstaciones();
  });

  // Central mapa en la península
  $('#centrarPeninsula').click(function() {
    centrarPeninsula();
  });

  // Central mapa en Canarias
  $('#centrarCanarias').click(function() {
    centrarCanarias();
  });

  // Ver mapa OSM
  $('#OSM').click(function() {
    OSMlayer.setVisible(true);
    OTMlayer.setVisible(false);
    PNOAlayer.setVisible(false);
    $('#OSM').addClass('mapButtonSelected');
    $('#OTM').removeClass('mapButtonSelected');
    $('#PNOA').removeClass('mapButtonSelected');
  });

  // Ver mapa OTM
  $('#OTM').click(function() {
    OSMlayer.setVisible(false);
    OTMlayer.setVisible(true);
    PNOAlayer.setVisible(false);
    $('#OSM').removeClass('mapButtonSelected');
    $('#OTM').addClass('mapButtonSelected');
    $('#PNOA').removeClass('mapButtonSelected');
  });

  // Ver mapa PNOA
  $('#PNOA').click(function() {
    OSMlayer.setVisible(false);
    OTMlayer.setVisible(false);
    PNOAlayer.setVisible(true);
    $('#OSM').removeClass('mapButtonSelected');
    $('#OTM').removeClass('mapButtonSelected');
    $('#PNOA').addClass('mapButtonSelected');

  });

  // Construir la tabla con la lista de estaciones cuando se hayan cargado las features en la capa vectorial
  estacionesSource.on('featuresloadend', function() {
    var tablaest = $('#tablaEstaciones').DataTable({
      "language": { "url": "https://cdn.datatables.net/plug-ins/9dcbecd42ad/i18n/Spanish.json" },
      "iDisplayLength": 200,
      "lengthChange": false,
      "info": false,
      "paging": false,
      "scrollCollapse": true,
      "scrollY": '450px',
      "order": [[0, "asc"]],
      "searching": true,
      "columns": [
        { className: "", type: 'diacritics-neutralise' },
        { className: "datosTabla" }
      ]
      }
    );
    var row = tablaest.row;
    estacionesSource.getFeatures().forEach(feature => {
      // var fila = ['<a alt="' + feature.get('name') + '" href="javascript:module.buildChart(' + feature.getId() + ', \'1h\')">'+ feature.get('name') + '</a>', feature.get('tasagamma')?feature.get('tasagamma').toFixed(1):''];
      var fila = ['<a alt="' + feature.get('name') + '" href="javascript:" id="stid_' + feature.getId() + '" class="enlaceEstacion">'+ feature.get('name') + '</a>', feature.get('tasagamma')?feature.get('tasagamma').toFixed(1):''];
      row.add(fila).draw(true);
    });
    // tablaest.columns.adjust().draw();
  });

  // Quitar el reloj de espera cuando la página se hay cargado
  quitarReloj();
});

$(document).on("click", ".enlaceEstacion", function() {
  const stid = this.id.replace(/^stid_/, "");
  buildChart(parseInt(stid), '1h');
});


